import React, { useEffect, useRef, useState } from 'react';

const CustomVideoPlayer = ({ src, title, subtitle, isCinemaMode, onToggleCinema, onPlayStateChange, onVideoEnd }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [playbackRate, setPlaybackRate] = useState(1);
    const controlsTimeoutRef = useRef(null);
    const youTubePlayerRef = useRef(null);
    const isYouTubeRef = useRef(false);

    // Extract YouTube video ID
    const getVideoId = (url) => {
        if (!url) return null;
        try {
            const parsed = new URL(url);
            if (parsed.hostname.includes('youtu.be')) {
                return parsed.pathname.replace('/', '').trim();
            }
            if (parsed.hostname.includes('youtube.com')) {
                return parsed.searchParams.get('v');
            }
        } catch (error) {
            return null;
        }
        return null;
    };

    // Load YouTube IFrame API
    useEffect(() => {
        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            document.body.appendChild(tag);
        }

        const videoId = getVideoId(src);
        if (videoId) {
            isYouTubeRef.current = true;

            const onYouTubeIframeAPIReady = () => {
                youTubePlayerRef.current = new window.YT.Player(playerRef.current, {
                    videoId: videoId,
                    width: '100%',
                    height: '100%',
                    playerVars: {
                        autoplay: 0,
                        controls: 0,
                        modestbranding: 1,
                        rel: 0,
                        showinfo: 0,
                        iv_load_policy: 3,
                        fs: 0,
                        playsinline: 1,
                        origin: window.location.origin
                    },
                    events: {
                        onReady: onPlayerReady,
                        onStateChange: onPlayerStateChange
                    }
                });
            };

            if (window.YT && window.YT.Player) {
                onYouTubeIframeAPIReady();
            } else {
                window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
            }
        }

        return () => {
            if (youTubePlayerRef.current) {
                youTubePlayerRef.current.destroy();
                youTubePlayerRef.current = null;
            }
        };
    }, [src]);

    const onPlayerReady = (event) => {
        setDuration(event.target.getDuration());
    };

    const onPlayerStateChange = (event) => {
        const playerStatus = event.data;
        if (playerStatus === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
            startControlsAutoHide();
            if (onPlayStateChange) {
                onPlayStateChange(true);
            }
        } else if (playerStatus === window.YT.PlayerState.ENDED) {
            setIsPlaying(false);
            clearControlsAutoHide();
            if (onPlayStateChange) {
                onPlayStateChange(false);
            }
            if (onVideoEnd) {
                onVideoEnd(); // Trigger activity page redirect
            }
        } else {
            setIsPlaying(false);
            clearControlsAutoHide();
            if (onPlayStateChange) {
                onPlayStateChange(false);
            }
        }
    };

    // Update current time for YouTube
    useEffect(() => {
        let interval;
        if (isPlaying && youTubePlayerRef.current) {
            interval = setInterval(() => {
                const time = youTubePlayerRef.current.getCurrentTime();
                setCurrentTime(time);
            }, 500);
        }
        return () => clearInterval(interval);
    }, [isPlaying]);

    const startControlsAutoHide = () => {
        clearControlsAutoHide();
        controlsTimeoutRef.current = setTimeout(() => {
            setShowControls(false);
        }, 3000);
    };

    const clearControlsAutoHide = () => {
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
            controlsTimeoutRef.current = null;
        }
    };

    const handleMouseMove = () => {
        setShowControls(true);
        if (isPlaying) {
            startControlsAutoHide();
        }
    };

    const handlePlayPause = () => {
        if (youTubePlayerRef.current) {
            if (isPlaying) {
                youTubePlayerRef.current.pauseVideo();
                setIsPlaying(false);
                if (onPlayStateChange) {
                    onPlayStateChange(false);
                }
            } else {
                youTubePlayerRef.current.playVideo();
                setIsPlaying(true);
                if (onPlayStateChange) {
                    onPlayStateChange(true);
                }
            }
        }
    };

    const handleProgress = (e) => {
        if (youTubePlayerRef.current) {
            const newTime = parseFloat(e.target.value);
            youTubePlayerRef.current.seekTo(newTime);
            setCurrentTime(newTime);
        }
    };

    const handleVolume = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (youTubePlayerRef.current) {
            youTubePlayerRef.current.setVolume(newVolume * 100);
        }
    };

    const handlePlaybackRate = (rate) => {
        setPlaybackRate(rate);
        if (youTubePlayerRef.current) {
            youTubePlayerRef.current.setPlaybackRate(rate);
        }
    };

    const handleFullscreen = () => {
        if (!isFullscreen) {
            if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            } else if (containerRef.current.webkitRequestFullscreen) {
                containerRef.current.webkitRequestFullscreen();
            } else if (containerRef.current.msRequestFullscreen) {
                containerRef.current.msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
            setIsFullscreen(false);
        }
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const containerStyle = {
        ...styles.container,
        ...(isCinemaMode ? styles.cinemaMode : {}),
        ...(isFullscreen ? styles.fullscreenMode : {})
    };

    return (
        <div ref={containerRef} style={containerStyle}>
            {/* Top Bar */}
            <div style={styles.topBar}>
                <div style={styles.topBarTitle}>
                    <div style={styles.labelText}>Now Playing</div>
                    <div style={styles.titleText}>{title || 'Select a lesson to start watching'}</div>
                    {subtitle && <div style={styles.subtitleText}>{subtitle}</div>}
                </div>
                <button style={styles.cinemaBtn} onClick={onToggleCinema}>
                    {isCinemaMode ? 'Exit Cinema' : 'Cinema Mode'}
                </button>
            </div>

            {/* Video Container */}
            <div
                style={styles.videoContainer}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => {
                    if (isPlaying) setShowControls(false);
                }}
            >
                {/* YouTube IFrame */}
                <div
                    ref={playerRef}
                    style={styles.iframeWrapper}
                    id="youtube-player"
                />

                {/* Controls Overlay */}
                <div
                    style={styles.controlsOverlay}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => setShowControls(true)}
                    onMouseLeave={() => {
                        if (isPlaying) setShowControls(false);
                    }}
                >
                    {/* Gradient fade at bottom */}
                    <div style={styles.gradientFade} />

                    {/* Control Bar */}
                    {showControls && (
                        <div style={styles.controlBar}>
                            {/* Progress Bar */}
                            <div style={styles.progressContainer}>
                                <input
                                    type="range"
                                    min="0"
                                    max={duration || 100}
                                    value={currentTime || 0}
                                    onChange={handleProgress}
                                    style={styles.progressBar}
                                />
                            </div>

                            {/* Bottom Controls */}
                            <div style={styles.controls}>
                                {/* Left Side */}
                                <div style={styles.controlsLeft}>
                                    <button style={styles.controlBtn} onClick={handlePlayPause}>
                                        {isPlaying ? '⏸' : '▶'}
                                    </button>

                                    <div style={styles.volumeControl}>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.1"
                                            value={volume}
                                            onChange={handleVolume}
                                            style={styles.volumeSlider}
                                        />
                                    </div>

                                    <div style={styles.timeDisplay}>
                                        <span>{formatTime(currentTime)}</span>
                                        <span style={styles.timeSeparator}>·</span>
                                        <span>{formatTime(duration)}</span>
                                    </div>
                                </div>

                                {/* Right Side */}
                                <div style={styles.controlsRight}>
                                    {/* Speed Control */}
                                    <div style={styles.speedControl}>
                                        <button
                                            style={playbackRate === 0.75 ? styles.speedBtnActive : styles.speedBtn}
                                            onClick={() => handlePlaybackRate(0.75)}
                                        >
                                            0.75x
                                        </button>
                                        <button
                                            style={playbackRate === 1 ? styles.speedBtnActive : styles.speedBtn}
                                            onClick={() => handlePlaybackRate(1)}
                                        >
                                            1x
                                        </button>
                                        <button
                                            style={playbackRate === 1.5 ? styles.speedBtnActive : styles.speedBtn}
                                            onClick={() => handlePlaybackRate(1.5)}
                                        >
                                            1.5x
                                        </button>
                                        <button
                                            style={playbackRate === 2 ? styles.speedBtnActive : styles.speedBtn}
                                            onClick={() => handlePlaybackRate(2)}
                                        >
                                            2x
                                        </button>
                                    </div>

                                    <button style={styles.controlBtn} onClick={handleFullscreen}>
                                        {isFullscreen ? '⛶' : '⛶'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Play Overlay (when not playing) */}
                    {!isPlaying && (
                        <button style={styles.playOverlay} onClick={handlePlayPause}>
                            <span style={styles.playIcon}>▶</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        position: 'relative',
        width: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#0b1220',
        border: '1px solid rgba(39, 176, 91, 0.12)',
        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.25)',
        marginBottom: '20px'
    },
    cinemaMode: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        borderRadius: 0,
        marginBottom: 0
    },
    fullscreenMode: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
        borderRadius: 0
    },
    topBar: {
        padding: '18px 22px',
        background: 'linear-gradient(90deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)',
        color: '#e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px'
    },
    topBarTitle: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    labelText: {
        fontSize: '12px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: '#27b05b'
    },
    titleText: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#e2e8f0'
    },
    subtitleText: {
        fontSize: '13px',
        color: '#94a3b8'
    },
    cinemaBtn: {
        background: 'rgba(39, 176, 91, 0.15)',
        border: '1px solid rgba(39, 176, 91, 0.5)',
        color: '#e2f7ea',
        padding: '8px 12px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '600'
    },
    videoContainer: {
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        background: 'radial-gradient(circle at top, rgba(39, 176, 91, 0.12), rgba(15, 23, 42, 0.9))',
        overflow: 'hidden'
    },
    iframeWrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
    },
    controlsOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end'
    },
    gradientFade: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '120px',
        background: 'linear-gradient(to top, rgba(11, 18, 32, 0.9), transparent)',
        pointerEvents: 'none'
    },
    controlBar: {
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column'
    },
    progressContainer: {
        padding: '8px 16px 0',
        background: 'transparent'
    },
    progressBar: {
        width: '100%',
        height: '6px',
        borderRadius: '3px',
        background: 'linear-gradient(to right, #27b05b 0%, #27b05b 18%, rgba(148, 163, 184, 0.3) 18%, rgba(148, 163, 184, 0.3) 100%)',
        cursor: 'pointer',
        WebkitAppearance: 'none',
        appearance: 'none',
        outline: 'none'
    },
    controls: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px 15px',
        gap: '16px'
    },
    controlsLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    controlsRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    controlBtn: {
        background: 'transparent',
        border: 'none',
        color: '#e2e8f0',
        fontSize: '18px',
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: '6px',
        transition: 'background 0.2s',
        '&:hover': {
            background: 'rgba(39, 176, 91, 0.3)'
        }
    },
    volumeControl: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    volumeSlider: {
        width: '80px',
        height: '4px',
        borderRadius: '2px',
        background: 'rgba(148, 163, 184, 0.3)',
        cursor: 'pointer',
        WebkitAppearance: 'none',
        appearance: 'none',
        outline: 'none'
    },
    timeDisplay: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: '#cbd5e1',
        fontWeight: '500'
    },
    timeSeparator: {
        opacity: 0.5
    },
    speedControl: {
        display: 'flex',
        gap: '6px'
    },
    speedBtn: {
        background: 'transparent',
        border: '1px solid rgba(148, 163, 184, 0.4)',
        color: '#cbd5e1',
        fontSize: '11px',
        fontWeight: '600',
        padding: '4px 8px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    speedBtnActive: {
        background: '#27b05b',
        border: '1px solid #27b05b',
        color: '#0b1220',
        fontSize: '11px',
        fontWeight: '600',
        padding: '4px 8px',
        borderRadius: '6px',
        cursor: 'pointer'
    },
    playOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 5,
        background: 'rgba(15, 23, 42, 0.7)',
        border: '2px solid rgba(39, 176, 91, 0.6)',
        color: '#e2f7ea',
        padding: '16px',
        borderRadius: '999px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '70px',
        height: '70px'
    },
    playIcon: {
        fontSize: '28px',
        marginLeft: '4px'
    }
};

export default CustomVideoPlayer;
